/**
 * Comma-separated value lists, the serialization every multi-value filter in
 * the app uses to keep a selection in one URL search param (and, for the
 * insights components, in a markdown-embedded attr).
 *
 * This is the codec only — writing the param is the caller's job, because the
 * callers disagree about how: `useMultiQueryState` owns a `setSearchParams`
 * call per param, while the insights filters batch every key into one
 * navigation through `useInsightsControls`'s `setParams` so that `location.state`
 * survives, as `packages/app/CLAUDE.md` requires.
 */

/**
 * Join values for storage in a URL search param. An empty list serializes to
 * `""`, which the param setters treat as "delete this param".
 */
export function serializeCommaList(values: string[]): string {
  return values.join(",");
}

/**
 * Split a comma-separated param into distinct values, dropping blanks and
 * surrounding whitespace and keeping first-seen order.
 *
 * A bare value — the form bookmarked before a filter accepted several — parses
 * to a single-element list, so old links keep working without a migration.
 * Deduping matches the insights resolver's `normalizeIdFilter` and keeps a
 * hand-edited URL from rendering the same selection twice.
 */
export function parseCommaList(raw: string | null | undefined): string[] {
  const values = (raw ?? "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
  return [...new Set(values)];
}
